import {  Paginated } from "@feathersjs/feathers/lib";
import { DataType, IQuiz, IStep, QuizValidationError, Services, shuffleArray, TaskType, ValidateQuizRequest, ValidateQuizResponse } from "types";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { Course, Data, Task } from ".";
import client from "../feathers";


export class Quiz extends Data<IQuiz> implements IQuiz {

  course_id:       number  = null;
  num_correct:     number  = null; // Number of correct answers needed to pass
  num_tasks:       number  = null;
  step_id:         number  = null;
  title:           string  = null;
  final:           boolean = false;
  tasks?:          Task[]  = [];
  is_control_quiz: boolean = false;
  show_results:    boolean | null = null;

  // Quiz validation state for taking a course
  is_validating:   boolean = false;
  correct_answers: number  = -1; // Number of correct answers given by the user
  cur_task_idx:    number  = 0;


  constructor(data: IQuiz, public course: Course) {
    // The super constructor takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Quiz, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      course_id:       observable,
      num_correct:     observable,
      num_tasks:       observable,
      step_id:         observable,
      final:           observable,
      title:           observable,
      tasks:           observable,
      is_validating:   observable,
      correct_answers: observable,
      cur_task_idx:    observable,
      is_control_quiz: observable,
      show_results:    observable,

      isValid:            computed,
      getValidationError: computed,
      alreadyPassed:      computed,
      isPassed:           computed,
      isCompleted:        computed,
      getCurTask:         computed,
      hasColumnTasks:     computed,
      showResults:        computed,

      reset:    action,
      nextTask: action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }
  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/

  public static get = async (id?: number): Promise<Quiz | Quiz[]> => {
    try {
      if(id) {
        return Quiz.create(await client.service(Services.Quiz).get(id));
      } else {
        const quizes: Paginated<IQuiz> = await client.service(Services.Quiz).find();
        if(!quizes.total) return [];
        return quizes.data.map(q => Quiz.create(q));
      }
    } catch (err) {
      console.error("Failed to load Quizes", err);
      return null;
    }
  }

  public static create(data: IQuiz, course?: Course): Quiz {
    return new Quiz(data, course);
  }

  protected fromData(data: IQuiz): void {
    super.fromData(data);
    this.course_id       = data.course_id && Number(data.course_id);
    this.step_id         = data.step_id && Number(data.step_id);
    this.num_correct     = data.num_correct;
    this.num_tasks       = data.num_tasks;
    this.title           = data.title;
    this.final           = data.final;
    this.tasks           = (data.tasks && data.tasks.map(t => Task.create(t)))?.sort((a, b) => a.id - b.id) || this.tasks || [] ;
    this.is_control_quiz = data.is_control_quiz;
    this.show_results    = data.show_results;
  }

  public toData(): any {
    return {
      ...super.toData(),
      course_id:       this.course_id || null,
      num_correct:     this.num_correct || null,
      num_tasks:       this.num_tasks || null,
      step_id:         this.step_id || null,
      title:           this.title || null,
      final:           this.final || null,
      is_control_quiz: this.is_control_quiz || null,
      show_results:    this.show_results || null,
    };
  }

  public toPatchData(patch: Partial<IStep>): any {
    return {
      ...super.toPatchData(patch),
      course_id:       "course_id" in patch ? this.course_id :             undefined,
      num_correct:     "num_correct" in patch ? this.num_correct :         undefined,
      num_tasks:       "num_tasks" in patch ? this.num_tasks :             undefined,
      step_id:         "step_id" in patch ? this.step_id :                 undefined,
      title:           "title" in patch ? this.title :                     undefined,
      final:           "final" in patch ? this.final :                     undefined,
      is_control_quiz: "is_control_quiz" in patch ? this.is_control_quiz:  undefined,
      show_results:    "show_results" in patch ? this.show_results:        undefined,
    };
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/

  public get canEdit(): boolean {
    return true;
  }

  /**
   * @return true if the quiz is valid and ready to be published by the editor.
   *         
   * Note: Use @getValidationError to receive the error message that explains 
   *       why the quiz is invalid. 
   * 
   * A quiz is valid if:
   * - it is setup to displayed at least one task
   * - it provides at least as many tasks as set in @num_tasks to randomly select to display for the user
   * - the pass constraint @num_correct is not larger than the number of displayed tasks @num_tasks
   * - all tasks are valid
   */
  public get isValid(): boolean {
    return (
      this.tasks.length && 
      this.tasks.length >= this.num_tasks && 
      this.num_correct  <= this.num_tasks && 
      !this.tasks.some(t => !t.isValid)
    );
  }

  /**
   * @return validation error message key to display information about 
   *         why the task is invalid. 
   */
  public get getValidationError(): QuizValidationError {
    if(!this.num_tasks)
      return QuizValidationError.TasksRequired;
    else if(this.num_correct  > this.num_tasks)
      return QuizValidationError.TooManyCorrectPassConstraint;
    else if(this.tasks.length < this.num_tasks)
      return QuizValidationError.InsufficientTasks;
    else if(this.tasks.some(t => !t.isValid)) 
      return QuizValidationError.InvalidTask;
    return QuizValidationError.Valid;
  }

  /**
   * @return true if the user has already passed this quiz
   */
  public get alreadyPassed(): boolean {
    return this.isCurrentTaskColumn
      ? false
      : this.course.getStepMap[this.step_id].isCompleted && !this.is_control_quiz;
  }

  /**
   * @return true if the user passes this quiz
   */
  public get isPassed(): boolean {
    const correctlyAnswered = this.correct_answers;
    const passingThreshold = this.num_correct;
    return correctlyAnswered >= passingThreshold;
  }

  /**
   * @return if show results is null, don't show results for control quizzes
   *        and column quizes by default. Otherwise show results for all other quizes.
   *        Otherwise, return the value of show_results.
   */
  public get showResults(): boolean {
    if(this.show_results === null){
      if (this.is_control_quiz || this.isCurrentTaskColumn) {
        return false;
      } else {
        return true;
      }
    } else {
      return this.show_results;
    }
  }


  /**
   * @return true if the user answered all questions to complete this quiz
   */
  public get isCompleted(): boolean {
    return Boolean(this.tasks?.length) && !this.tasks.some(t => !t.isComplete);
  }

  public get hasColumnTasks(): boolean {
    return this.tasks.some(t => t.type === TaskType.Column);
  }

  public get getCurTask(): Task | null {
    return this.tasks.length
      ? this.tasks[this.cur_task_idx || 0]
      : null;
  }

  public get isCurrentTaskColumn(): boolean {
    return this.getCurTask? this.getCurTask.type === TaskType.Column: false;
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  addTask = async (): Promise<Task> => {
    try {
      const task: Task = Task.create({
        course_id:   this.course_id,
        question:    '',
        quiz_id:     this.id,
        step_id:     this.step_id,
        type:        TaskType.Single,
        always_show: false,
        answer:      "",
        num_choice:  1,
      });
      await task.save();
      runInAction(() => this.tasks.push(task));
      return task;
    } catch (err) {
      console.error("Failed to add task", err);
      return null;
    }
  }

  removeTask = async (task: Task): Promise<boolean> => {
    try {
      await task.remove();
      runInAction(() => {
        this.tasks = this.tasks.filter(t => t.id !== task.id) || [];
      })
    } catch(err) {
      console.error("Failed to remove task", err);
      return false;
    }
    return true;
  }

  /**
   * Sends the current quiz off to the backend for validation.
   * The backend will then take care of checking each individual answer and 
   * record the user progress. 
   * 
   * @return number of correct answers or -1 if something went wrong. 
   */
  validate = async (): Promise<boolean> => {
    if(!this.isCompleted) return false;

    try {
      runInAction(() => this.is_validating = true);
      const res: ValidateQuizResponse = await client.service(Services.Quiz).validate(this.tasks.reduce((res: ValidateQuizRequest, val) => {
        switch(val.type) {
          case TaskType.Column:
          case TaskType.Input:
            res[val.id] = val.input;
            return res;
          case TaskType.Single:
            res[val.id] = val.getSelected[0].id;
            return res;
          case TaskType.Multi:
            res[val.id] = val.choices.reduce((res: {[choice_id: number]: boolean}, choice) => {
              res[choice.id] = choice.is_selected;
              return res;
            }, {});
            return res;
        }
      }, {quiz_id: this.id}));
      runInAction(() => {
        // Remove the old, stored progress
        this.course.course_progress = this.course.course_progress.filter(cp => Number(cp.step_id) !== Number(res.progress.step_id));

        // Add the new one with the results given by the backend
        this.course.course_progress.push({
          attempts:  Number(res.progress.attempts),
          completed: res.progress.completed && new Date(res.progress.completed),
          course_id: Number(res.progress.course_id),
          step_id:   Number(res.progress.step_id),
          user_id:   Number(res.progress.user_id),
        });

        // Resort progress and update progress value if applicable 
        this.course.sortProgress();
        this.course.progress = Math.round((this.course.course_progress.length / this.course.steps.length)*100);
      });

      runInAction(() => {
        this.correct_answers = res.num_correct;
        this.is_validating   = false;
      })
      return true;
    } catch (err) {
      console.error("Failed to validate quiz", err);
      return false;
    }
  }

  shuffle = () => {
    this.tasks = shuffleArray(this.tasks);
    for(const t of this.tasks) {
      t.choices = shuffleArray(t.choices);
    }
  }

  reset = () => {
    this.is_validating   = false;
    this.cur_task_idx    = 0;
    this.correct_answers = -1;
    this.tasks.forEach(t => t.reset());
  }

  nextTask = () => {
    if(this.cur_task_idx < (this.tasks.length -1)) 
      this.cur_task_idx++;
  }
}
